import { __rest } from "tslib";
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { ChevronRight, More } from '@wix/wix-ui-icons-common';
import PopoverMenu from '../PopoverMenu';
import Button from '../Button';
import IconButton from '../IconButton';
import Tooltip from '../Tooltip';
import { dataHooks } from './constants';
import HoverSlot from './HoverSlot';
import { st, classes } from './TableActionCell.st.css.js';
import { TooltipCommonProps } from '../common/PropTypes/TooltipCommon';
import { placements } from '../Popover';
import deprecationLog from '../utils/deprecationLog';
const blurElementWithoutFocusVisible = element => {
    if (process.env.NODE_ENV !== 'test' && !element.matches(':focus-visible')) {
        document.activeElement.blur();
    }
};
/* eslint-disable react/prop-types */
const renderPrimaryAction = (_a, size) => {
    var _b;
    var { text, skin, onClick, disabled, prefixIcon, suffixIcon, as, priority, visibility } = _a, rest = __rest(_a, ["text", "skin", "onClick", "disabled", "prefixIcon", "suffixIcon", "as", "priority", "visibility"]);
    const visibilityPriorityMap = {
        always: 'secondary',
        onHover: 'primary',
    };
    return (React.createElement(Button, Object.assign({ disabled: disabled, skin: visibility ? undefined : skin, priority: (_b = visibilityPriorityMap[visibility]) !== null && _b !== void 0 ? _b : priority, dataHook: dataHooks.primaryAction, size: size, prefixIcon: prefixIcon, suffixIcon: suffixIcon, className: st(classes.action, { size }), onClick: event => {
            onClick();
            blurElementWithoutFocusVisible(event.target);
            // Making sure we don't also trigger onRowClick
            event.stopPropagation();
        }, as: as }, rest), text));
};
/* eslint-enable react/prop-types */
const renderVisibleActions = (actions, size) => actions.map(({ text, icon, onClick, dataHook, disabled, disabledDescription, tooltipProps, }, index) => (React.createElement(Tooltip, Object.assign({ key: index, dataHook: dataHook || dataHooks.visibleActionTooltip, disabled: disabled && disabledDescription === '', content: disabled && Boolean(disabledDescription) ? disabledDescription : text }, tooltipProps),
    React.createElement(IconButton, { dataHook: dataHooks.visibleAction, skin: "inverted", disabled: disabled, className: st(classes.action, { size }), onClick: event => {
            onClick();
            blurElementWithoutFocusVisible(event.target);
            event.stopPropagation();
        }, size: size, ariaLabel: text }, icon))));
const renderHiddenActions = (actions, popoverMenuProps, size, moreActionsTooltipText) => (React.createElement(PopoverMenu, Object.assign({ dataHook: dataHooks.popoverMenu, appendTo: "parent", placement: "top", textSize: "small", triggerElement: ({ toggle, isOpen, ref }) => (React.createElement(Tooltip, { content: moreActionsTooltipText, disabled: !moreActionsTooltipText }, ({ onFocus, onBlur }) => (React.createElement(IconButton, { onBlur: onBlur, skin: "inverted", className: st(classes.action, { size }), dataHook: dataHooks.triggerElement, size: size, onClick: toggle, ref: ref, ariaLabel: moreActionsTooltipText || undefined, "aria-haspopup": "menu", "aria-expanded": isOpen, onFocus: event => {
            onFocus(event, {
                blur: () => blurElementWithoutFocusVisible(event.target),
            });
        } },
        React.createElement(More, null))))) }, popoverMenuProps), actions.map(({ text, icon, onClick, disabled, dataHook, divider, skin, subtitle }, index) => !divider ? (React.createElement(PopoverMenu.MenuItem, { skin: skin !== null && skin !== void 0 ? skin : 'dark', key: index, dataHook: dataHook || dataHooks.popoverMenuItem, prefixIcon: icon, onClick: () => onClick(), text: text, disabled: disabled, subtitle: subtitle })) : (React.createElement(PopoverMenu.Divider, null)))));
const renderPlaceholder = size => (React.createElement(IconButton, { dataHook: dataHooks.placeholder, size: size, skin: "inverted", tabIndex: -1 },
    React.createElement(ChevronRight, null)));
const TableActionCell = ({ onClick = () => { }, dataHook, primaryAction = null, secondaryActions = [], numOfVisibleSecondaryActions = 0, alwaysShowSecondaryActions = false, moreActionsTooltipText, popoverMenuProps, size = 'medium', }) => {
    var _a;
    useEffect(() => {
        if (primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.skin) {
            deprecationLog('<TableActionCell/> - prop "primaryAction" with key "skin" is deprecated and will be removed in next major release. It has been replaced by the visibility property, which switches skin properties automatically based on visibility options.');
        }
    }, [primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.skin]);
    const visibleActions = secondaryActions.slice(0, numOfVisibleSecondaryActions);
    const hiddenActions = secondaryActions.slice(numOfVisibleSecondaryActions);
    return (React.createElement("span", { "data-hook": dataHook, className: classes.root, "data-more-actions-tooltip-text": moreActionsTooltipText },
        primaryAction && (React.createElement(HoverSlot, { "data-hook": dataHooks.primaryActionWrapper, display: (_a = primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.visibility) !== null && _a !== void 0 ? _a : 'onHover' }, renderPrimaryAction(primaryAction, size))),
        visibleActions.length > 0 && (React.createElement(HoverSlot, { "data-hook": dataHooks.visibleActionsWrapper, display: alwaysShowSecondaryActions ? 'always' : 'onHover' }, renderVisibleActions(visibleActions, size))),
        hiddenActions.length > 0 && (React.createElement("div", { onClick: e => e.stopPropagation() },
            React.createElement(HoverSlot, { display: "always" }, renderHiddenActions(hiddenActions, popoverMenuProps, size, moreActionsTooltipText)))),
        primaryAction &&
            primaryAction.visibility !== 'always' &&
            (!secondaryActions.length ||
                secondaryActions.length === numOfVisibleSecondaryActions) && (React.createElement(HoverSlot, { display: "notOnHover", className: classes.placeholderIcon }, renderPlaceholder(size)))));
};
TableActionCell.displayName = 'TableActionCell';
TableActionCell.propTypes = {
    /** Applies a data-hook HTML attribute to be used in the tests */
    dataHook: PropTypes.string,
    /**
       Controls the behaviour and appearance of a primary action:
     - `text` specifies action label
     - `skin` <b>deprecated</b> property that was previously used to control the visual appearance of a button. It has been replaced by the visibility property, which switches skin properties automatically based on visibility options.
     - `onClick` defines a callback function which is called on button click
     - `disabled` restrict user interactions
     - `prefixIcon` add an icon in front of a button label
     - `suffixIcon` add an icon after button label
     - `as` renders button as another component or a DOM tag
     - `visibility` specifies whether the primary action button is visible. When the value is set to "onHover", the button is only visible when the mouse hovers over it, and it appears as a primary button. When the value is set to "always", the button is always visible and appears as a secondary button.
     */
    primaryAction: PropTypes.shape({
        text: PropTypes.string.isRequired,
        skin: PropTypes.oneOf(['standard', 'inverted']),
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        prefixIcon: PropTypes.element,
        suffixIcon: PropTypes.element,
        as: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object,
            PropTypes.string,
        ]),
        visibility: PropTypes.oneOf(['onHover', 'always']),
    }),
    /**
     Controls the behaviour and appearance of secondary actions:
  
     Action object containing
     - `text` specifies action label to be shown in the tooltip or popover menu
     - `icon` specifies icon of an action
     - `onClick` defines a callback function which is called on button click
     - `subtitle` a subtitle for the action
     - `disabled` restrict user interactions
     - `dataHook` applies a data-hook HTML attribute to be used in the tests
     - `disabledDescription` indicates an explanatory text string to display in tooltip when action is visible and disabled
     - `tooltipProps` controls the tooltip appearance
     - `skin` controls the skin of the item
  
     OR Divider object containing
     - `divider` displays a divider between the action items in popover menu
     */
    secondaryActions: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.shape({
            text: PropTypes.string.isRequired,
            icon: PropTypes.node.isRequired,
            onClick: PropTypes.func.isRequired,
            subtitle: PropTypes.string,
            disabled: PropTypes.bool,
            dataHook: PropTypes.string,
            disabledDescription: PropTypes.string,
            tooltipProps: PropTypes.shape(TooltipCommonProps),
            skin: PropTypes.oneOf(['dark', 'destructive']),
        }),
        PropTypes.shape({
            divider: PropTypes.bool.isRequired,
        }),
    ])),
    /** Specifies the number of secondary actions to display outside of the popover menu */
    numOfVisibleSecondaryActions: PropTypes.number,
    /** Specifies whether secondary actions should be visible all the time. By default secondary actions are visible on row hover only. */
    alwaysShowSecondaryActions: PropTypes.bool,
    /** Sets the tooltip text of more actions icon button */
    moreActionsTooltipText: PropTypes.string,
    /** Controls the appearance of secondary actions menu with all common popover menu props */
    popoverMenuProps: PropTypes.shape({
        /** The maximum width applied to the list */
        maxWidth: PropTypes.number,
        /** The minimum width applied to the list */
        minWidth: PropTypes.number,
        /** The maximum height value applied to the list */
        maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** Popover content z-index */
        zIndex: PropTypes.number,
        /** Moves popover content relative to the parent by x or y */
        moveBy: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
        /** The Popover's placement:
         *  * auto-start
         *  * auto
         *  * auto-end
         *  * top-start
         *  * top
         *  * top-end
         *  * right-start
         *  * right
         *  * right-end
         *  * bottom-end
         *  * bottom
         *  * bottom-start
         *  * left-end
         *  * left
         *  * left-start
         */
        placement: PropTypes.oneOf(placements),
        /** Changing text size */
        textSize: PropTypes.oneOf(['small', 'medium']),
        /** Enables text ellipsis on tight containers */
        ellipsis: PropTypes.bool,
        /** The Popover's appendTo */
        appendTo: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        /**
         * Whether to enable the flip behaviour. This behaviour is used to flip the `<Popover/>`'s placement
         * when it starts to overlap the target element (`<Popover.Element/>`).
         */
        flip: PropTypes.bool,
        /**
         * Whether to enable the fixed behaviour. This behaviour is used to keep the `<Popover/>` at it's
         * original placement even when it's being positioned outside the boundary.
         */
        fixed: PropTypes.bool,
        /** Whether to show the Popover's arrow */
        showArrow: PropTypes.bool,
    }),
    /** Controls the size of action buttons */
    size: PropTypes.oneOf(['tiny', 'small', 'medium']),
};
export default TableActionCell;
//# sourceMappingURL=TableActionCell.js.map